import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { CollectorsSort, CollectorsSortChips } from '@components/Collectors';
import { CollectorCard } from '@components/Collectors/Cards/CollectorCard';
import { ParamsStore, useParams } from '@context/params';
import type { FragmentType, VariablesOf } from '@graphql/reactQuery';
import {
  AllBackedArtistsDocument,
  BackedArtistSectionFragmentDoc,
  getFragment,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { useStableCallback } from '@hooks/useStableCallback';
import { CollectorsContainer, Container } from './Collectors';
import { SectionHeader } from './SectionHeader';
import { Subheader } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AllBackedArtists($id: UUID!, $pagination: ArtistCollectorCursorConnectionArgs) {
    user(id: $id) {
      id
      backedArtists(pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            nftsCount
            firstNftCollectedDate
            ...CollectorCardInfo
          }
        }
      }
    }
  }
`);

function backedArtistsVariables({
  userId,
  after,
  sort,
}: {
  userId: string;
  after: Maybe<string>;
  sort: CollectorsSort;
}): VariablesOf<typeof AllBackedArtistsDocument> {
  return {
    id: userId,
    pagination: {
      first: 40,
      after,
      sort:
        sort === 'top'
          ? {
              nftsCount: 'DESC',
            }
          : {
              firstNftCollectedDate: 'ASC',
            },
    },
  };
}

export const ArtistsBackedSection = ({
  user,
}: {
  user: FragmentType<BackedArtistSectionFragmentDoc>;
}) => {
  const userData = getFragment(BackedArtistSectionFragmentDoc, user);
  const userId = userData.id;

  const [sort, setSort] = useState<CollectorsSort>(CollectorsSort.TOP);

  const { sort: windowQuerySort } = useParams();

  useSafeLayoutEffect(() => {
    if (windowQuerySort != null) {
      if (windowQuerySort === CollectorsSort.EARLIEST) setSort(CollectorsSort.EARLIEST);
      else {
        ParamsStore.sort = null;
      }
    }
  }, [windowQuerySort]);

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });

  const {
    orderedList: backedArtists,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    isLoading,
  } = useInfiniteQuery(AllBackedArtistsDocument, {
    filterQueryKey: {
      sort,
      userId,
    },
    list({ user }) {
      return user?.backedArtists.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    getNextPageParam({ data: { user } }) {
      return (
        user?.backedArtists.pageInfo.hasNextPage && { after: user.backedArtists.pageInfo.endCursor }
      );
    },
    variables({ pageParam }) {
      return backedArtistsVariables({
        userId,
        after: pageParam?.after || null,
        sort,
      });
    },
    order:
      sort === 'top'
        ? [
            [v => v.nftsCount, v => v.firstNftCollectedDate, v => v.id],
            ['desc', 'asc', 'asc'],
          ]
        : [
            [v => v.firstNftCollectedDate, v => v.nftsCount, v => v.id],
            ['asc', 'desc', 'asc'],
          ],
    staleTime: 0,
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMore();
    }
  }, [sort, isAtBottom, loadMore, hasNextPage]);

  const onSetSort = useStableCallback((newSort: CollectorsSort) => {
    if (sort !== newSort) {
      setSort(newSort);

      switch (newSort) {
        case CollectorsSort.TOP: {
          ParamsStore.sort = null;
          break;
        }
        case CollectorsSort.EARLIEST: {
          ParamsStore.sort = newSort;
          break;
        }
        default: {
          break;
        }
      }
    }
  });

  return (
    <Container>
      <SectionHeader header="Artists Backed" userWebappUri={userData.webappUri} />
      <Subheader>
        <CollectorsSortChips sort={sort} setSort={onSetSort} />
      </Subheader>
      <CollectorsContainer>
        {backedArtists?.map((backedArtist, index) => (
          <CollectorCard
            key={backedArtist.id}
            collector={backedArtist}
            index={index + 1}
            sort={sort}
            seeAllPage
            variant="artistBacked"
            loading={isLoading}
          />
        ))}
        <div ref={bottomRef} />
      </CollectorsContainer>
    </Container>
  );
};
